import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Media } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { availableCountries, selectCountry } from '../../selectors/country';
import * as dashboardActions from '../../store/dashboard/actions';
import * as countriesActions from '../../store/countries/actions';
import colors from '../CommonStyle/colors';

const CountrySelector = ({ countryId }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const currentCountry = useSelector(selectCountry(countryId));
  const countries = useSelector(availableCountries);
  const dispatch = useDispatch();
  const filterInputRef = useRef(null);

  useEffect(() => {
    dispatch(countriesActions.syncCountries());
  }, [dispatch]);

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(filter.toLowerCase()),
  );

  const openDropdown = () => {
    setFilter('');
    setFocusedIndex(-1);
    setDropdownOpen(true);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
    setFilter('');
    setFocusedIndex(-1);
  };

  const toggle = () => {
    if (dropdownOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  useEffect(() => {
    if (dropdownOpen && filterInputRef.current) {
      setTimeout(() => filterInputRef.current?.focus(), 50);
    }
  }, [dropdownOpen]);

  const handleCountryChange = (newCountryId) => {
    if (newCountryId !== countryId) {
      dispatch(dashboardActions.changeCountry(newCountryId));
    }
    closeDropdown();
  };

  const handleFilterKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, filteredCountries.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target =
          focusedIndex >= 0
            ? filteredCountries[focusedIndex]
            : filteredCountries[0];
        if (target) handleCountryChange(target.id);
      } else if (e.key === 'Escape') {
        closeDropdown();
      }
    },
    [filteredCountries, focusedIndex],
  );

  if (countries.length <= 1) {
    return (
      <div className="d-inline-block mr-4 mini-stats-wid">
        <Media className="header-item">
          <div className="align-self-center mr-2">
            <span className="avatar-xs rounded-circle">
              <i className="bx bx-world font-size-22 text-info"></i>
            </span>
          </div>
          <div className="align-self-center mr-3">
            <h5 className="font-size-15 mt-0 mb-1">
              {currentCountry ? currentCountry.name : ''}
            </h5>
          </div>
          {process.env.REACT_APP_ENVIRONMENT === 'dev' && (
            <div className="align-self-center mr-3">
              <span className="badge badge-danger font-size-14">DEV</span>
            </div>
          )}
        </Media>
      </div>
    );
  }

  return (
    <div className="d-inline-block mr-4">
      <Dropdown isOpen={dropdownOpen} toggle={toggle}>
        <DropdownToggle
          tag="button"
          className="btn header-item waves-effect"
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            padding: '0.5rem',
          }}>
          <Media className="align-items-center">
            <div className="align-self-center mr-2">
              <span className="avatar-xs rounded-circle">
                <i className="bx bx-world font-size-22 text-info"></i>
              </span>
            </div>
            <div className="align-self-center mr-2">
              <h5 className="font-size-15 mt-0 mb-1">
                {currentCountry ? currentCountry.name : 'Select Country'}
              </h5>
            </div>
            <i className="mdi mdi-chevron-down"></i>
            {process.env.REACT_APP_ENVIRONMENT === 'dev' && (
              <div className="align-self-center ml-2">
                <span className="badge badge-danger font-size-14">DEV</span>
              </div>
            )}
          </Media>
        </DropdownToggle>
        <DropdownMenu
          right
          style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
          <div className="px-3 py-2" onKeyDown={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <i
                className="bx bx-search"
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.secondary,
                  fontSize: '15px',
                  pointerEvents: 'none',
                }}
              />
              <input
                ref={filterInputRef}
                type="text"
                className="form-control form-control-sm"
                placeholder="Filter countries..."
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setFocusedIndex(-1);
                }}
                onKeyDown={handleFilterKeyDown}
                style={{ paddingLeft: '30px' }}
              />
            </div>
          </div>
          {filteredCountries.length === 0 && (
            <DropdownItem disabled>No countries found</DropdownItem>
          )}
          {filteredCountries.map((country, index) => (
            <DropdownItem
              key={country.id}
              onClick={() => handleCountryChange(country.id)}
              active={country.id === countryId}
              style={
                index === focusedIndex
                  ? {
                      backgroundColor: colors.info,
                      color: '#fff',
                      outline: 'none',
                    }
                  : undefined
              }>
              <i className="bx bx-world font-size-16 align-middle mr-2"></i>
              {country.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default CountrySelector;
